import { ReactNode } from "react";
import { Icon } from "@iconify/react";
import { card, colors, PLAN_COLORS, PLAN_ORDER, type } from "../../styles";

export interface CustomerFilterState {
  query: string;
  plans: string[];
  states: string[];
  connect: string[];
  cancelingOnly: boolean;
}

export const EMPTY_FILTERS: CustomerFilterState = {
  query: "",
  plans: [],
  states: [],
  connect: [],
  cancelingOnly: false,
};

/** Stripe Connect account statuses, as stored on `stripeAccountStatus`. */
export const CONNECT_STATUSES = ["completed", "manual review", "incomplete", "pending"];

export const STATE_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "TRIAL", label: "Trial" },
];

const Chip = ({
  label,
  selected,
  onClick,
  dotColor,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  dotColor?: string;
}) => (
  <button
    onClick={onClick}
    aria-pressed={selected}
    style={{
      ...type.bodySmallMedium,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      color: colors.ink,
      background: selected ? colors.surfaceMuted : "transparent",
      border: `1px solid ${selected ? colors.borderStrong : colors.border}`,
      borderRadius: 999,
      padding: "5px 11px",
      cursor: "pointer",
      textTransform: "capitalize",
    }}
  >
    {dotColor && (
      <span
        style={{ width: 7, height: 7, borderRadius: 999, background: dotColor, flexShrink: 0 }}
      />
    )}
    {label}
  </button>
);

const FilterGroup = ({ label, children }: { label: string; children: ReactNode }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
    <span style={{ ...type.bodySmallMedium, color: colors.inkFaint }}>{label}</span>
    {children}
  </div>
);

const toggle = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter(item => item !== value) : [...list, value];

/**
 * Every filter is client-side over the single fetched page and serializes to
 * the URL, so a filtered view is linkable and survives a refresh.
 */
const CustomerFilters = ({
  filters,
  onChange,
  resultCount,
  totalCount,
}: {
  filters: CustomerFilterState;
  onChange: (next: CustomerFilterState) => void;
  resultCount: number;
  totalCount: number;
}) => {
  const anyActive =
    Boolean(filters.query) ||
    filters.plans.length > 0 ||
    filters.states.length > 0 ||
    filters.connect.length > 0 ||
    filters.cancelingOnly;

  return (
    <div
      style={{
        ...card,
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginBottom: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: "7px 11px",
            flex: 1,
            minWidth: 240,
          }}
        >
          <Icon icon="ion:search" height={15} style={{ color: colors.inkFaint }} />
          <input
            value={filters.query}
            onChange={event => onChange({ ...filters, query: event.target.value })}
            placeholder="Search name, username, or email"
            style={{
              ...type.bodyRegular,
              border: "none",
              outline: "none",
              flex: 1,
              color: colors.ink,
              background: "transparent",
              minWidth: 0,
            }}
          />
        </div>

        <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
          {resultCount === totalCount
            ? `${totalCount} customers`
            : `${resultCount} of ${totalCount} customers`}
        </span>

        {anyActive && (
          <button
            onClick={() => onChange(EMPTY_FILTERS)}
            style={{
              ...type.bodySmallMedium,
              color: colors.inkMuted,
              background: "transparent",
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              padding: "5px 10px",
              cursor: "pointer",
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <FilterGroup label="Plan">
          {PLAN_ORDER.map(plan => (
            <Chip
              key={plan}
              label={plan}
              dotColor={PLAN_COLORS[plan]}
              selected={filters.plans.includes(plan)}
              onClick={() => onChange({ ...filters, plans: toggle(filters.plans, plan) })}
            />
          ))}
        </FilterGroup>

        <FilterGroup label="Status">
          {STATE_OPTIONS.map(option => (
            <Chip
              key={option.value}
              label={option.label}
              selected={filters.states.includes(option.value)}
              onClick={() =>
                onChange({ ...filters, states: toggle(filters.states, option.value) })
              }
            />
          ))}
        </FilterGroup>

        <FilterGroup label="Connect">
          {CONNECT_STATUSES.map(status => (
            <Chip
              key={status}
              label={status}
              selected={filters.connect.includes(status)}
              onClick={() =>
                onChange({ ...filters, connect: toggle(filters.connect, status) })
              }
            />
          ))}
        </FilterGroup>

        <FilterGroup label="">
          <Chip
            label="Canceling at period end"
            selected={filters.cancelingOnly}
            onClick={() => onChange({ ...filters, cancelingOnly: !filters.cancelingOnly })}
          />
        </FilterGroup>
      </div>
    </div>
  );
};

export default CustomerFilters;
